import * as _ from "lodash";
import { pathToRegexp, compile } from "path-to-regexp";
import swal from "sweetalert2";
import config from "../../config";
import { serialize } from "../../service";

import { MODULE_ID } from "../../shared/moduleIds";

export const getMonitorPath = (query) => {
  const datetime = query.timestamp || query.datetime;
  return `/patient/${query.asmpid}/monitor${
    datetime ? `/event-history/${encodeURIComponent(datetime)}` : ""
  }`;
};

export const getMultiPatientViewPath = (query) => {
  const { view, listIndex, zoom, monitorZoom, phi } = query;
  return `/patients/surveil/${listIndex}?${new URLSearchParams({
    view,
    listIndex,
    zoom,
    monitorZoom,
    phi,
  }).toString()}`;
};

export function getInternalPaths() {
  const internalPaths = new Map();
  internalPaths.set("pm-mon", getMonitorPath);
  internalPaths.set("pm-mpv", getMultiPatientViewPath);
  return internalPaths;
}

/** TODO: Add when PM Trends is a route
 *  {asRoute: 'pm-tnd'}
 */
export const routes = [
  {
    asRoute: "dash",
    webRoute: "/patient/:asmpid/overview",
  },
  {
    asRoute: "census",
    webRoute: "/patients",
  },
  {
    asRoute: "ecg",
    webRoute: "/patient/:asmpid/ecgs/:ecgid?",
    moduleId: MODULE_ID.CARDIOLOGY,
  },
  {
    asRoute: "labs",
    webRoute: "/patient/:asmpid/labs",
    moduleId: MODULE_ID.ELECTRONIC_MEDICAL_RECORD,
  },
  {
    asRoute: "vitals",
    webRoute: "/patient/:asmpid/vitals",
    moduleId: MODULE_ID.ELECTRONIC_MEDICAL_RECORD,
  },
  {
    asRoute: "pm-evt",
    webRoute: "/patient/:asmpid/monitor/events",
    moduleId: MODULE_ID.PATIENT_MONITORING,
  },
  {
    asRoute: "pm-mon",
    webRoute:
      "/patient/:asmpid/monitor/(event|history|event-history)?/:timestamp?",
    moduleId: MODULE_ID.PATIENT_MONITORING,
  },
  {
    asRoute: "meds",
    webRoute: "/patient/:asmpid/meds",
    moduleId: MODULE_ID.ELECTRONIC_MEDICAL_RECORD,
  },
  {
    asRoute: "ob",
    webRoute: "/patient/:asmpid/ob-summary",
    moduleId: MODULE_ID.OBSTETRICS,
  },
  {
    asRoute: "pm-mpv",
    webRoute: "/patients/surveil/:listIndex?",
    moduleId: MODULE_ID.PATIENT_MONITORING,
  },
].map((route) => {
  const keys = [];
  const webRegexp = pathToRegexp(route.webRoute, keys);
  return {
    ...route,
    keys,
    webRegexp,
  };
});

export function findRouteByWebRegExp({ path }) {
  let matches;
  const route = routes.find((route) => (matches = path.match(route.webRegexp)));
  return { route, matches: matches?.filter(Boolean) };
}

export function findRouteByAsRoute({ url }) {
  const path = url.match(/airstripone:\/\/([^/]+)\/([^?]+)/);
  return routes.find((p) => p.asRoute === path[2]);
}

export function getRouteParams({ route, matches }) {
  const params = new Map();

  if (route.keys && matches) {
    route.keys.forEach((key, i) => {
      if (typeof key.name === "string") {
        params.set(key.name, matches[i + 1]);
      } else if (typeof key.name === "boolean") {
        params.set(key.name, matches[i + 1].toString());
      }
    });
  }

  const routeParams = Array.from(params).reduce((obj, [key, value]) => {
    if (value !== undefined) obj[key] = value;
    return obj;
  }, {});

  return routeParams;
}

export function serializeQuery({ route, matches }) {
  const routeParams = getRouteParams({ route, matches });

  if (typeof route.moduleId !== "undefined")
    routeParams.moduleid = route.moduleId;
  const toSerialize = {
    siteid: config.siteid,
    ...routeParams,
  };

  return serialize({ ...toSerialize });
}

/**
 * Similar to URLSearchParams, takes the tail end of a URL and converts the query params to an object.
 */
export function deserializeQuery({ query }) {
  let _query = _.last(query.split("?"));
  return _query.split("&").reduce((queryObj, pair) => {
    const [key, value] = pair.split("=");
    return { ...queryObj, [key]: decodeURIComponent(value) };
  }, {});
}
/**
 * Translates a given share link into a route that this app understands
 */
export function translateShareLinkToRoute({ url, internalPaths }) {
  const _paths = internalPaths || getInternalPaths();
  const query = deserializeQuery({ query: url });

  const route = findRouteByAsRoute({ url });

  if (!route) {
    swal.fire({
      title: "No supported path",
      text: "The share link did not supply a module path supported by this web application.",
      icon: "error",
    });
    return;
  }

  if (_paths.has(route.asRoute)) {
    return _paths.get(route.asRoute)(query);
  } else {
    return compile(route.webRoute)(query);
  }
}

/**
 * Translates a given AS1 route into share link
 */
export function translateRouteToShareLink({ path, shareLinkDomain }) {
  const { route, matches } = findRouteByWebRegExp({ path });
  if (!route) {
    swal.fire({
      title: "Not Sharable",
      text: "The application cannot share this path.",
      icon: "error",
    });
  } else {
    const query = serializeQuery({
      route,
      matches,
    });
    return `airstripone://${shareLinkDomain}/${route.asRoute}${query}`;
  }
}
